/**
 * DiningStaffManager - Manage kitchen and POS staff
 * Includes role-based permissions for KDS and POS access
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users, Plus, Edit2, Trash2, ChefHat, CreditCard, Search,
  Key, Shield, UserCheck, X, Save, Eye, EyeOff, Upload
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import QuickStaffOnboarding from '../QuickStaffOnboarding';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DiningStaffManager = () => {
  const queryClient = useQueryClient();
  const [showStaffDialog, setShowStaffDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [activeTab, setActiveTab] = useState('kitchen');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Staff form state
  const [staffForm, setStaffForm] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    staff_type: 'kitchen',
    role: 'line_cook',
    is_active: true
  });

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch staff
  const { data: staff = [], isLoading } = useQuery({
    queryKey: ['dining-staff'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/dining/staff`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch roles
  const { data: rolesData } = useQuery({
    queryKey: ['dining-roles'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/dining/roles`, { headers });
      if (!res.ok) return { kitchen_roles: [], pos_roles: [] };
      return res.json();
    }
  });

  const kitchenRoles = rolesData?.kitchen_roles || [];
  const posRoles = rolesData?.pos_roles || [];

  // Create staff
  const createStaffMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/dining/staff`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create staff');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['dining-staff']);
      setShowStaffDialog(false);
      resetForm();
      toast.success('Staff member created!');
    },
    onError: (error) => toast.error(error.message)
  });

  // Update staff
  const updateStaffMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/api/admin/dining/staff/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['dining-staff']);
      setShowStaffDialog(false);
      resetForm();
      toast.success('Staff updated!');
    },
    onError: (error) => toast.error(error.message)
  });

  // Delete staff
  const deleteStaffMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/api/admin/dining/staff/${id}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error('Failed to delete');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['dining-staff']);
      toast.success('Staff deleted!');
    }
  });

  // Reset password
  const resetPasswordMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/api/admin/dining/staff/${id}/reset-password`, {
        method: 'POST',
        headers
      });
      if (!res.ok) throw new Error('Failed to reset password');
      return res.json();
    },
    onSuccess: (data) => {
      toast.success(data.message);
    }
  });

  const resetForm = () => {
    setStaffForm({
      email: '',
      password: '',
      name: '',
      phone: '',
      staff_type: activeTab,
      role: activeTab === 'kitchen' ? 'line_cook' : 'cashier',
      is_active: true
    });
    setSelectedStaff(null);
    setShowPassword(false);
  };

  const openEditDialog = (staffMember) => {
    setSelectedStaff(staffMember);
    setStaffForm({
      email: staffMember.email,
      password: '',
      name: staffMember.name,
      phone: staffMember.phone || '',
      staff_type: staffMember.staff_type,
      role: staffMember.role,
      is_active: staffMember.is_active !== false
    });
    setShowStaffDialog(true);
  };

  const openCreateDialog = (type) => {
    resetForm();
    setStaffForm(prev => ({
      ...prev,
      staff_type: type,
      role: type === 'kitchen' ? 'line_cook' : 'cashier'
    }));
    setShowStaffDialog(true);
  };

  const handleSave = () => {
    if (!staffForm.name || !staffForm.email) {
      toast.error('Name and email are required');
      return;
    }
    if (!selectedStaff && !staffForm.password) {
      toast.error('Password is required for new staff');
      return;
    }

    if (selectedStaff) {
      const updateData = {
        name: staffForm.name,
        phone: staffForm.phone,
        role: staffForm.role,
        is_active: staffForm.is_active
      };
      updateStaffMutation.mutate({ id: selectedStaff.id, data: updateData });
    } else {
      createStaffMutation.mutate(staffForm);
    }
  };

  const kitchenStaff = staff.filter(s => s.staff_type === 'kitchen');
  const posStaff = staff.filter(s => s.staff_type === 'pos');

  const filteredKitchenStaff = kitchenStaff.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPosStaff = posStaff.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const roleColors = {
    head_chef: 'bg-purple-100 text-purple-800',
    line_cook: 'bg-orange-100 text-orange-800',
    prep_cook: 'bg-yellow-100 text-yellow-800',
    kitchen_helper: 'bg-gray-100 text-gray-800',
    pos_manager: 'bg-blue-100 text-blue-800',
    cashier: 'bg-green-100 text-green-800',
    order_taker: 'bg-teal-100 text-teal-800',
    viewer: 'bg-gray-100 text-gray-800'
  };

  const StaffCard = ({ staffMember }) => (
    <Card className={`${!staffMember.is_active ? 'opacity-60' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              staffMember.staff_type === 'kitchen' ? 'bg-orange-100' : 'bg-blue-100'
            }`}>
              {staffMember.staff_type === 'kitchen' ? (
                <ChefHat className="h-6 w-6 text-orange-600" />
              ) : (
                <CreditCard className="h-6 w-6 text-blue-600" />
              )}
            </div>
            <div>
              <h3 className="font-semibold">{staffMember.name}</h3>
              <p className="text-sm text-gray-500">{staffMember.email}</p>
              <Badge className={roleColors[staffMember.role] || 'bg-gray-100'}>
                {staffMember.role_name || staffMember.role}
              </Badge>
            </div>
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => openEditDialog(staffMember)}>
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              onClick={() => resetPasswordMutation.mutate(staffMember.id)}
              title="Reset password"
            >
              <Key className="h-4 w-4" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              className="text-red-600"
              onClick={() => deleteStaffMutation.mutate(staffMember.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* Permissions */}
        <div className="mt-3 pt-3 border-t">
          <p className="text-xs font-medium text-gray-500 mb-1">Permissions:</p>
          <div className="flex flex-wrap gap-1">
            {staffMember.permissions?.slice(0, 4).map((perm, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {perm.split('.')[1]}
              </Badge>
            ))}
            {staffMember.permissions?.length > 4 && (
              <Badge variant="outline" className="text-xs">
                +{staffMember.permissions.length - 4}
              </Badge>
            )}
          </div>
        </div>

        {staffMember.last_login && (
          <p className="text-xs text-gray-400 mt-2">
            Last login: {new Date(staffMember.last_login).toLocaleDateString()}
          </p>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="dining-staff-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6 text-blue-600" />
            Dining Staff Management
          </h2>
          <p className="text-sm text-gray-500">Manage kitchen and POS staff with role-based permissions</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search staff..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="kitchen" className="gap-2">
              <ChefHat className="h-4 w-4" />
              Kitchen Staff ({kitchenStaff.length})
            </TabsTrigger>
            <TabsTrigger value="pos" className="gap-2">
              <CreditCard className="h-4 w-4" />
              POS Staff ({posStaff.length})
            </TabsTrigger>
          </TabsList>
          <div className="flex gap-2">
            <QuickStaffOnboarding staffType={activeTab} />
            <Button onClick={() => openCreateDialog(activeTab)} data-testid="add-staff-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add {activeTab === 'kitchen' ? 'Kitchen' : 'POS'} Staff
            </Button>
          </div>
        </div>

        {/* Kitchen Staff Tab */}
        <TabsContent value="kitchen" className="mt-4">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : filteredKitchenStaff.length === 0 ? (
            <Card className="py-12">
              <CardContent className="text-center">
                <ChefHat className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No kitchen staff yet</p>
                <Button variant="link" onClick={() => openCreateDialog('kitchen')}>
                  Add your first kitchen staff member
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredKitchenStaff.map((s) => (
                <StaffCard key={s.id} staffMember={s} />
              ))}
            </div>
          )}
          
          {/* Kitchen Roles Reference */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Kitchen Roles Reference
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {kitchenRoles.map((role) => (
                  <div key={role.id} className="p-3 bg-gray-50 rounded-lg">
                    <Badge className={roleColors[role.id]}>{role.name}</Badge>
                    <ul className="mt-2 text-xs text-gray-600 space-y-1">
                      {role.permissions?.map((p, i) => (
                        <li key={i} className="flex items-center gap-1">
                          <UserCheck className="h-3 w-3 text-green-500" />
                          {p.split('.')[1]}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* POS Staff Tab */}
        <TabsContent value="pos" className="mt-4">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : filteredPosStaff.length === 0 ? (
            <Card className="py-12">
              <CardContent className="text-center">
                <CreditCard className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No POS staff yet</p>
                <Button variant="link" onClick={() => openCreateDialog('pos')}>
                  Add your first POS staff member
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredPosStaff.map((s) => (
                <StaffCard key={s.id} staffMember={s} />
              ))}
            </div>
          )}

          {/* POS Roles Reference */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Shield className="h-4 w-4" />
                POS Roles Reference
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {posRoles.map((role) => (
                  <div key={role.id} className="p-3 bg-gray-50 rounded-lg">
                    <Badge className={roleColors[role.id]}>{role.name}</Badge>
                    <ul className="mt-2 text-xs text-gray-600 space-y-1">
                      {role.permissions?.map((p, i) => (
                        <li key={i} className="flex items-center gap-1">
                          <UserCheck className="h-3 w-3 text-green-500" />
                          {p.split('.')[1]}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Staff Dialog */}
      <Dialog open={showStaffDialog} onOpenChange={setShowStaffDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedStaff ? 'Edit Staff' : `New ${staffForm.staff_type === 'kitchen' ? 'Kitchen' : 'POS'} Staff`}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={staffForm.name}
                onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
                placeholder="Full name"
              />
            </div>
            
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={staffForm.email}
                onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                placeholder="email@example.com"
                disabled={!!selectedStaff}
              />
            </div>
            
            {!selectedStaff && (
              <div>
                <Label>Password *</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={staffForm.password}
                    onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })}
                    placeholder="Initial password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}

            <div>
              <Label>Phone</Label>
              <Input
                value={staffForm.phone}
                onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })}
                placeholder="Phone number"
              />
            </div>

            <div>
              <Label>Role</Label>
              <Select
                value={staffForm.role}
                onValueChange={(v) => setStaffForm({ ...staffForm, role: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {staffForm.staff_type === 'kitchen' ? (
                    kitchenRoles.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))
                  ) : (
                    posRoles.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={staffForm.is_active}
                onCheckedChange={(checked) => setStaffForm({ ...staffForm, is_active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStaffDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleSave} 
              disabled={createStaffMutation.isPending || updateStaffMutation.isPending}
            >
              <Save className="h-4 w-4 mr-2" />
              {selectedStaff ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiningStaffManager;
